import React, { useEffect, useMemo, useState } from 'react';
import { Box, Rating, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { reviewsApi } from '../../api/reviews';
import {
  PageHeader,
  PageFilters,
  DataTable,
  TablePaginationBar,
  StatusBadge,
} from '../../components/ui';
import type { Column } from '../../components/ui';
import { PAGE_SIZE } from '../../constants';
import { formatDate, paginate, matchesSearch } from '../../utils';
import type { Review, ReviewStatus } from '../../types';

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: 'all' },
  { label: 'Active', value: 'Active' },
  { label: 'Hidden', value: 'Hidden' },
];

const RATING_OPTIONS = [
  { label: 'All Ratings', value: 'all' },
  { label: '5 Stars', value: '5' },
  { label: '4 Stars', value: '4' },
  { label: '3 Stars', value: '3' },
  { label: '2 Stars', value: '2' },
  { label: '1 Star', value: '1' },
];

const PARTY_OPTIONS = [
  { label: 'All Parties', value: 'all' },
  { label: 'Customer reviewing Provider', value: 'Customer' },
  { label: 'Provider reviewing Customer', value: 'Provider' },
];

const ReviewsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Review[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [rating, setRating] = useState('all');
  const [partyType, setPartyType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    reviewsApi
      .getReviews({ page: 1, limit: 1000 })
      .then((res) => setRows(res.reviews))
      .catch(() => toast.error('Failed to load reviews'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      const matches =
        !search.trim() ||
        matchesSearch(row.id, search) ||
        matchesSearch(row.jobId, search) ||
        matchesSearch(row.reviewer, search) ||
        matchesSearch(row.reviewedParty, search) ||
        matchesSearch(row.text, search);
      const statusOk = status === 'all' || row.status === (status as ReviewStatus);
      const ratingOk = rating === 'all' || row.rating === Number(rating);
      const partyOk = partyType === 'all' || row.reviewerType === partyType;
      const d = new Date(row.date);
      const fromOk = !dateFrom || d >= new Date(dateFrom);
      const toOk = !dateTo || d <= new Date(`${dateTo}T23:59:59`);
      return matches && statusOk && ratingOk && partyOk && fromOk && toOk;
    });
  }, [rows, search, status, rating, partyType, dateFrom, dateTo]);

  useEffect(() => {
    setPage(1);
  }, [search, status, rating, partyType, dateFrom, dateTo]);

  const paged = paginate(filtered, page, PAGE_SIZE);

  const columns: Column<Review>[] = [
    {
      id: 'id',
      label: 'ID',
      minWidth: 100,
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.id}
        </Typography>
      ),
    },
    {
      id: 'jobId',
      label: 'Job',
      minWidth: 100,
      render: (row) => row.jobId,
    },
    {
      id: 'reviewer',
      label: 'Reviewer',
      minWidth: 140,
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {row.reviewer}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.reviewerType}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'reviewedParty',
      label: 'Reviewed Party',
      minWidth: 140,
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {row.reviewedParty}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.reviewedPartyType}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'rating',
      label: 'Rating',
      minWidth: 130,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Rating value={row.rating} readOnly size="small" />
          <Typography variant="caption">{row.rating}</Typography>
        </Box>
      ),
    },
    {
      id: 'text',
      label: 'Review',
      minWidth: 180,
      render: (row) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            maxWidth: 220,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.text}
        </Typography>
      ),
    },
    {
      id: 'date',
      label: 'Date',
      minWidth: 110,
      render: (row) => formatDate(row.date),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  return (
    <Box>
      <PageHeader title="Reviews & Ratings" subtitle="Search and filter marketplace reviews" />
      <PageFilters
        search={search}
        searchPlaceholder="Search reviews…"
        onSearchChange={setSearch}
        filters={[
          {
            id: 'rating',
            label: 'Rating',
            value: rating,
            options: RATING_OPTIONS,
            onChange: setRating,
          },
          {
            id: 'status',
            label: 'Status',
            value: status,
            options: STATUS_OPTIONS,
            onChange: setStatus,
          },
          {
            id: 'party',
            label: 'Parties',
            value: partyType,
            options: PARTY_OPTIONS,
            onChange: setPartyType,
          },
        ]}
      >
        <TextField
          size="small"
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
      </PageFilters>
      <DataTable
        columns={columns}
        rows={paged}
        loading={loading}
        getRowId={(row) => row.id}
        emptyTitle="No reviews found"
      />
      <TablePaginationBar
        page={page}
        pageSize={PAGE_SIZE}
        total={filtered.length}
        onChange={setPage}
      />
    </Box>
  );
};

export default ReviewsPage;
